import React, { useMemo } from 'react';
import { SessionTimeline } from 'shared/types';
import { buildClockModel } from './buildClockModel';
import SessionClock from './SessionClock';
import SessionStats from './SessionStats';
import SessionTimelineStrip from './SessionTimelineStrip';
import {
  CLOCK_COLORS,
  ClockWrap,
  ClockColumn,
  StatsColumn,
  Legend,
  LegendItem,
  Swatch,
  EmptyNote,
} from './styles';

type Props = {
  timeline: SessionTimeline;
  /** Scope the view to a single attendee; null aggregates all rows. */
  userId?: string | null;
};

/**
 * The "session clock" body: radial clock + stat row + linear strip for one
 * attendee. Falls back to a note when there's no station-level data (e.g. some
 * product_c sessions, or sessions that never entered a learning experience).
 */
const SessionTimelineView: React.FC<Props> = ({ timeline, userId = null }): JSX.Element => {
  const model = useMemo(() => buildClockModel(timeline, userId), [timeline, userId]);

  if (!model || model.totalSec <= 0) {
    return <EmptyNote>No timeline data available for this session.</EmptyNote>;
  }

  if (model.stationCount === 0) {
    return (
      <EmptyNote>
        This session has no station-level activity recorded
        {timeline.dataSource ? ` (${timeline.dataSource})` : ''}. Total time: just{' '}
        {Math.round(model.totalSec)}s in the lobby without entering a learning experience.
      </EmptyNote>
    );
  }

  return (
    <div>
      <ClockWrap>
        <ClockColumn>
          <SessionClock model={model} />
        </ClockColumn>
        <StatsColumn>
          <SessionStats model={model} />
        </StatsColumn>
      </ClockWrap>

      <SessionTimelineStrip model={model} />

      <Legend>
        <LegendItem>
          <Swatch $color={CLOCK_COLORS.active} /> Active learning
        </LegendItem>
        <LegendItem>
          <Swatch $color={CLOCK_COLORS.idle} /> In station, idle
        </LegendItem>
        <LegendItem>
          <Swatch $color={CLOCK_COLORS.lobby} /> Lobby / gaps
        </LegendItem>
        <LegendItem>
          <Swatch $color={CLOCK_COLORS.correct} /> Correct answer
        </LegendItem>
        <LegendItem>
          <Swatch $color={CLOCK_COLORS.incorrect} /> Incorrect answer
        </LegendItem>
      </Legend>
    </div>
  );
};

export default SessionTimelineView;
